import { useDraggable } from "@dnd-kit/core";
import { GripVertical, LogIn, LogOut, Phone, StickyNote } from "lucide-react";
import { GuestHistory } from "./GuestHistory";

export const BookingChip = ({ booking, color, late, onStatus, onNoteSaved, draggable = true }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: booking.id, data: { booking }, disabled: !draggable,
  });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  const arrived = booking.status === "ARRIVED";
  const departed = booking.status === "DEPARTED";
  return (
    <div ref={setNodeRef} style={{ ...style, borderLeftColor: color }} data-testid={`cal-booking-${booking.code}`}
      className={`relative bg-white rounded-md border border-l-4 p-2.5 text-xs shadow-sm ${isDragging ? "z-50 shadow-xl opacity-90" : ""} ${
        late ? "ring-2 ring-red-400" : ""} ${departed ? "opacity-60" : ""}`}>
      <div className="flex items-start gap-1.5">
        {draggable && (
          <button {...listeners} {...attributes} aria-label="Drag to another spot" data-testid={`cal-drag-${booking.code}`}
            className="cursor-grab active:cursor-grabbing text-stone-400 hover:text-stone-700 touch-none -ml-1 mt-0.5">
            <GripVertical size={14} />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <GuestHistory booking={booking} onNoteSaved={onNoteSaved} />
          <div className="text-stone-500 mt-0.5 flex flex-wrap gap-x-2">
            <span className="font-medium text-stone-700">{booking.time}</span>
            <span>{booking.guests} pax</span>
            <span className="font-mono">{booking.code}</span>
            {booking.source === "walk-in" && <span className="text-amber-700">walk-in</span>}
            {booking.deposit_amount > 0 && (
              <span className={booking.payment_status === "paid" ? "text-emerald-700" : "text-red-600"}>€{booking.deposit_amount} {booking.payment_status}</span>
            )}
          </div>
          {booking.phone && (
            <a href={`tel:${booking.phone.replace(/\s/g, "")}`} className="flex items-center gap-1 truncate text-stone-500 hover:text-stone-900 mt-0.5">
              <Phone size={11} /> {booking.phone}
            </a>
          )}
          {booking.history?.visits > 0 && (
            <p className="text-amber-700 font-medium mt-0.5" data-testid={`cal-regular-${booking.code}`}>Regular · {booking.history.visits} past visit{booking.history.visits > 1 ? "s" : ""}</p>
          )}
          {booking.history?.guest_note && (
            <p className="flex items-start gap-1 bg-amber-50 text-amber-900 rounded px-1.5 py-1 mt-1" data-testid={`cal-guest-note-${booking.code}`}>
              <StickyNote size={11} className="mt-0.5 shrink-0" /> <span className="line-clamp-2">{booking.history.guest_note}</span>
            </p>
          )}
          {booking.notes && <p className="text-amber-700 mt-1 line-clamp-2">“{booking.notes}”</p>}
          {late && <p className="text-red-600 font-medium mt-1" data-testid={`cal-late-${booking.code}`}>Not arrived · past 13:00</p>}
          <div className="flex flex-wrap gap-1.5 mt-2">
            <button onClick={() => onStatus(booking, arrived ? "BOOKED" : "ARRIVED")} data-testid={`cal-arrive-${booking.code}`}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${arrived ? "bg-amber-300 text-stone-900" : "bg-stone-100 hover:bg-amber-100"}`}>
              <LogIn size={12} /> {arrived ? "Arrived" : "Check-in"}
            </button>
            <button onClick={() => onStatus(booking, departed ? "ARRIVED" : "DEPARTED")} data-testid={`cal-depart-${booking.code}`}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${departed ? "bg-[#b09873] text-white" : "bg-stone-100 hover:bg-stone-200"}`}>
              <LogOut size={12} /> {departed ? "Departed" : "Depart"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
